import {
  Course,
  ContentCategory,
  ContentCategoryType,
  EventListing,
  ImageUploadFolder,
  InterestedUserRecord,
  RegistrationRecord,
  RegistrationStatus,
  RegistrationType,
  SportsAcademy,
  TeachingMode,
  Tutor,
  TutorStatus,
  UploadedImage,
  UploadedVideo,
  VideoUploadFolder,
  Workshop,
} from '@/types';
import { API_BASE } from '@/config/api';

type CourseWire = Course & { show_on_landing?: boolean };
type ApiError = { error?: string };

async function readApiError(res: Response, fallback: string) {
  try {
    const data = (await res.json()) as ApiError;
    return data.error || fallback;
  } catch {
    return fallback;
  }
}

async function apiFetch(path: string, init: RequestInit = {}) {
  const isFormDataBody = typeof FormData !== 'undefined' && init.body instanceof FormData;
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...(isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
      ...(init.headers || {}),
    },
  });

  return res;
}

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];

const normalizeTeachingMode = (value: unknown, fallback: TeachingMode): TeachingMode => {
  const mode = String(value || '').trim();
  if (mode === 'Online' || mode === 'Offline' || mode === 'Hybrid') {
    return mode;
  }
  return fallback;
};

const readShowOnLanding = (course: CourseWire): boolean | undefined => {
  if (typeof course.showOnLanding === 'boolean') return course.showOnLanding;
  if (typeof course.show_on_landing === 'boolean') return course.show_on_landing;
  return undefined;
};

const normalizeCourse = (course: CourseWire): Course => {
  const showOnLanding = readShowOnLanding(course);
  return {
    ...course,
    audience: normalizeStringArray(course.audience),
    teachingMode: normalizeTeachingMode(course.teachingMode, 'Online'),
    prerequisite: String(course.prerequisite || '').trim(),
    isFree: course.isFree === true,
    sections: Array.isArray(course.sections)
      ? course.sections.map((section) => ({
          ...section,
          lessons: Array.isArray(section.lessons)
            ? section.lessons.map((lesson) => ({
                ...lesson,
                description: String(lesson.description || '').trim(),
                duration: Number(lesson.duration) || 0,
              }))
            : [],
        }))
      : [],
    ...(showOnLanding !== undefined ? { showOnLanding } : {}),
  };
};

const normalizeSports = (item: SportsAcademy): SportsAcademy => ({
  ...item,
  audience: normalizeStringArray(item.audience),
  level: String(item.level || '').trim() || 'Open to All',
  teachingMode: normalizeTeachingMode(item.teachingMode, 'Offline'),
  sports: normalizeStringArray(item.sports),
  highlights: normalizeStringArray(item.highlights),
  facilities: normalizeStringArray(item.facilities),
  programs: Array.isArray(item.programs) ? item.programs : [],
  isFree: item.isFree === true,
});

const normalizeEventItem = <T extends EventListing | Workshop>(item: T): T => ({
  ...item,
  category: item.category === 'workshop' ? 'workshop' : 'event',
  audience: normalizeStringArray(item.audience),
  level: String(item.level || '').trim() || 'Open to All',
  teachingMode: normalizeTeachingMode(item.teachingMode, 'Offline'),
  tags: normalizeStringArray(item.tags),
  isFree: item.isFree === true,
}) as T;

const normalizeRegistration = (record: RegistrationRecord): RegistrationRecord => ({
  ...record,
  itemType:
    record.itemType === 'course' || record.itemType === 'sports' || record.itemType === 'event'
      ? record.itemType
      : 'unknown',
  itemTypeLabel:
    record.itemType === 'sports'
      ? 'Sports'
      : record.itemType === 'course'
        ? 'Course'
        : record.itemType === 'event'
          ? 'Event'
          : 'Unknown',
  itemId: String(record.itemId || '').trim(),
  itemName: String(record.itemName || '').trim() || 'Untitled item',
  name: String(record.name || '').trim() || 'Unknown user',
  email: String(record.email || '').trim(),
  phone: String(record.phone || '').trim() || '-',
  notes: String(record.notes || '').trim(),
  status:
    record.status === 'Contacted' || record.status === 'Converted' || record.status === 'Closed'
      ? record.status
      : 'New',
});

const writeCoursePayload = (courseData: Partial<Course>): Record<string, unknown> => {
  const showOnLanding = courseData.showOnLanding;
  return {
    ...courseData,
    audience: normalizeStringArray(courseData.audience),
    teachingMode: normalizeTeachingMode(courseData.teachingMode, 'Online'),
    prerequisite: String(courseData.prerequisite || '').trim(),
    ...(typeof showOnLanding === 'boolean'
      ? { showOnLanding, show_on_landing: showOnLanding }
      : {}),
  };
};

const buildQueryString = (params: Record<string, string | undefined>) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value?.trim()) {
      query.set(key, value.trim());
    }
  });
  const value = query.toString();
  return value ? `?${value}` : '';
};

export const categoryApi = {
  getCategories: async (
    options: { type?: ContentCategoryType; signal?: AbortSignal } = {}
  ): Promise<ContentCategory[]> => {
    const query = buildQueryString({ type: options.type });
    const res = await apiFetch(`/categories${query}`, { signal: options.signal, headers: {} });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to fetch categories'));
    return res.json();
  },

  createCategory: async (data: { name: string; type: ContentCategoryType }): Promise<ContentCategory> => {
    const res = await apiFetch('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to create category'));
    return res.json();
  },

  deleteCategory: async (id: string): Promise<void> => {
    const res = await apiFetch(`/categories/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) {
      throw new Error(await readApiError(res, 'Failed to delete category'));
    }
  },
};

export const courseApi = {
  getAllCourses: async (signal?: AbortSignal): Promise<Course[]> => {
    const res = await apiFetch('/courses', { signal, headers: {} });
    if (!res.ok) throw new Error('Failed to fetch courses');
    const data = (await res.json()) as CourseWire[];
    return data.map(normalizeCourse);
  },

  getMyCourses: async (signal?: AbortSignal): Promise<Course[]> => {
    const res = await apiFetch('/courses?mine=true', { signal, headers: {} });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to fetch your courses'));
    const data = (await res.json()) as CourseWire[];
    return data.map(normalizeCourse);
  },

  getCourseBySlug: async (slug: string, signal?: AbortSignal): Promise<Course | undefined> => {
    const res = await apiFetch(`/courses/slug/${encodeURIComponent(slug)}`, { signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch course');
    const data = (await res.json()) as CourseWire;
    return normalizeCourse(data);
  },

  getCourseById: async (id: string, signal?: AbortSignal): Promise<Course | undefined> => {
    const res = await apiFetch(`/courses/${id}`, { signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch course');
    const data = (await res.json()) as CourseWire;
    return normalizeCourse(data);
  },

  searchCourses: async (query: string, signal?: AbortSignal): Promise<Course[]> => {
    const res = await apiFetch(`/courses?search=${encodeURIComponent(query)}`, { signal, headers: {} });
    if (!res.ok) throw new Error('Failed to search courses');
    const data = (await res.json()) as CourseWire[];
    return data.map(normalizeCourse);
  },

  filterByLevel: async (level: string): Promise<Course[]> => {
    const courses = await courseApi.getAllCourses();
    if (level === 'all') return courses;
    return courses.filter((course) => course.level === level);
  },

  createCourse: async (courseData: Partial<Course>): Promise<Course> => {
    const res = await apiFetch('/courses', {
      method: 'POST',
      body: JSON.stringify(writeCoursePayload(courseData)),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to create course'));
    return normalizeCourse((await res.json()) as CourseWire);
  },

  updateCourse: async (id: string, courseData: Partial<Course>): Promise<Course> => {
    const res = await apiFetch(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(writeCoursePayload(courseData)),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update course'));
    return normalizeCourse((await res.json()) as CourseWire);
  },

  deleteCourse: async (id: string): Promise<void> => {
    const res = await apiFetch(`/courses/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) throw new Error(await readApiError(res, 'Failed to delete course'));
  },
};

export const sportsApi = {
  getAllSports: async (options: { signal?: AbortSignal; search?: string } = {}): Promise<SportsAcademy[]> => {
    const query = buildQueryString({ search: options.search });
    const res = await apiFetch(`/sports${query}`, { signal: options.signal, headers: {} });
    if (!res.ok) throw new Error('Failed to fetch sports academies');
    return ((await res.json()) as SportsAcademy[]).map(normalizeSports);
  },

  getSportsBySlug: async (slug: string, signal?: AbortSignal): Promise<SportsAcademy | undefined> => {
    const res = await apiFetch(`/sports/slug/${slug}`, { signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch sports academy');
    return normalizeSports((await res.json()) as SportsAcademy);
  },

  getSportsById: async (id: string, signal?: AbortSignal): Promise<SportsAcademy | undefined> => {
    const res = await apiFetch(`/sports/${id}`, { signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch sports academy');
    return normalizeSports((await res.json()) as SportsAcademy);
  },

  createSports: async (data: Partial<SportsAcademy>): Promise<SportsAcademy> => {
    const res = await apiFetch('/sports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to create sports academy'));
    return normalizeSports((await res.json()) as SportsAcademy);
  },

  updateSports: async (id: string, data: Partial<SportsAcademy>): Promise<SportsAcademy> => {
    const res = await apiFetch(`/sports/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update sports academy'));
    return normalizeSports((await res.json()) as SportsAcademy);
  },

  deleteSports: async (id: string): Promise<void> => {
    const res = await apiFetch(`/sports/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) throw new Error(await readApiError(res, 'Failed to delete sports academy'));
  },
};

export const eventApi = {
  getAllEvents: async (
    options: { signal?: AbortSignal; category?: 'event' | 'workshop'; search?: string } = {}
  ): Promise<EventListing[]> => {
    const query = buildQueryString({ category: options.category, search: options.search });
    const res = await apiFetch(`/events${query}`, { signal: options.signal, headers: {} });
    if (!res.ok) throw new Error('Failed to fetch events');
    return ((await res.json()) as EventListing[]).map(normalizeEventItem);
  },

  getEventBySlug: async (
    slug: string,
    options: { signal?: AbortSignal; category?: 'event' | 'workshop' } = {}
  ): Promise<EventListing | undefined> => {
    const query = buildQueryString({ category: options.category });
    const res = await apiFetch(`/events/slug/${slug}${query}`, { signal: options.signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch event');
    return normalizeEventItem((await res.json()) as EventListing);
  },

  getEventById: async (
    id: string,
    options: { signal?: AbortSignal; category?: 'event' | 'workshop' } = {}
  ): Promise<EventListing | undefined> => {
    const query = buildQueryString({ category: options.category });
    const res = await apiFetch(`/events/${id}${query}`, { signal: options.signal, headers: {} });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch event');
    return normalizeEventItem((await res.json()) as EventListing);
  },

  createEvent: async (data: Partial<EventListing>): Promise<EventListing> => {
    const res = await apiFetch('/events', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to create event'));
    return normalizeEventItem((await res.json()) as EventListing);
  },

  updateEvent: async (id: string, data: Partial<EventListing>): Promise<EventListing> => {
    const res = await apiFetch(`/events/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update event'));
    return normalizeEventItem((await res.json()) as EventListing);
  },

  deleteEvent: async (id: string, category?: 'event' | 'workshop'): Promise<void> => {
    const query = buildQueryString({ category });
    const res = await apiFetch(`/events/${id}${query}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) throw new Error(await readApiError(res, 'Failed to delete event'));
  },
};

export const workshopApi = {
  getAllWorkshops: async (signal?: AbortSignal): Promise<Workshop[]> => {
    const items = await eventApi.getAllEvents({ signal, category: 'workshop' });
    return items.map((item) => normalizeEventItem(item as Workshop));
  },

  getWorkshopBySlug: async (slug: string, signal?: AbortSignal): Promise<Workshop | undefined> => {
    const item = await eventApi.getEventBySlug(slug, { signal, category: 'workshop' });
    return item ? normalizeEventItem(item as Workshop) : undefined;
  },

  getWorkshopById: async (id: string, signal?: AbortSignal): Promise<Workshop | undefined> => {
    const item = await eventApi.getEventById(id, { signal, category: 'workshop' });
    return item ? normalizeEventItem(item as Workshop) : undefined;
  },

  createWorkshop: async (data: Partial<Workshop>): Promise<Workshop> => {
    const item = await eventApi.createEvent({ ...data, category: 'workshop' });
    return normalizeEventItem(item as Workshop);
  },

  updateWorkshop: async (id: string, data: Partial<Workshop>): Promise<Workshop> => {
    const item = await eventApi.updateEvent(id, { ...data, category: 'workshop' });
    return normalizeEventItem(item as Workshop);
  },

  deleteWorkshop: async (id: string): Promise<void> => {
    await eventApi.deleteEvent(id, 'workshop');
  },
};

export const tutorApi = {
  getAllTutors: async (
    options: { signal?: AbortSignal; scope?: 'management' } = {}
  ): Promise<Tutor[]> => {
    const params = new URLSearchParams();
    if (options.scope) {
      params.set('scope', options.scope);
    }

    const query = params.toString();
    const res = await apiFetch(`/tutors${query ? `?${query}` : ''}`, {
      signal: options.signal,
      headers: {},
    });
    if (!res.ok) throw new Error('Failed to fetch tutors');
    return res.json();
  },

  getTutorById: async (
    id: string,
    options: { signal?: AbortSignal; scope?: 'management' } = {}
  ): Promise<Tutor | undefined> => {
    const params = new URLSearchParams();
    if (options.scope) {
      params.set('scope', options.scope);
    }

    const query = params.toString();
    const res = await apiFetch(`/tutors/${id}${query ? `?${query}` : ''}`, {
      signal: options.signal,
      headers: {},
    });
    if (res.status === 404) return undefined;
    if (!res.ok) throw new Error('Failed to fetch tutor');
    return res.json();
  },

  getMyTutorProfiles: async (signal?: AbortSignal): Promise<Tutor[]> => {
    const res = await apiFetch('/tutors/me', { signal, headers: {} });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to fetch your tutor profiles'));
    return res.json();
  },

  getMyTutorProfile: async (signal?: AbortSignal): Promise<Tutor | null> => {
    const profiles = await tutorApi.getMyTutorProfiles(signal);
    return profiles[0] || null;
  },

  createTutor: async (data: Partial<Tutor>): Promise<Tutor> => {
    const res = await apiFetch('/tutors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to create tutor'));
    return res.json();
  },

  updateTutor: async (id: string, data: Partial<Tutor>): Promise<Tutor> => {
    const res = await apiFetch(`/tutors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update tutor'));
    return res.json();
  },

  updateTutorStatus: async (id: string, status: TutorStatus): Promise<Tutor> => {
    const res = await apiFetch(`/tutors/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update tutor status'));
    return res.json();
  },

  deleteTutor: async (id: string): Promise<void> => {
    const res = await apiFetch(`/tutors/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) throw new Error(await readApiError(res, 'Failed to delete tutor'));
  },
};

export const registrationApi = {
  getAll: async (
    options: {
      search?: string;
      status?: RegistrationStatus | 'all';
      itemType?: RegistrationType | 'all';
      signal?: AbortSignal;
    } = {}
  ): Promise<RegistrationRecord[]> => {
    const query = buildQueryString({
      search: options.search,
      status: options.status && options.status !== 'all' ? options.status : undefined,
      itemType: options.itemType && options.itemType !== 'all' ? options.itemType : undefined,
    });

    const res = await apiFetch(`/registrations${query}`, {
      signal: options.signal,
      headers: {},
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to fetch registrations'));
    return ((await res.json()) as RegistrationRecord[]).map(normalizeRegistration);
  },

  create: async (payload: {
    itemType: RegistrationType;
    itemId: string;
    name: string;
    email?: string;
    phone: string;
    notes?: string;
  }): Promise<RegistrationRecord> => {
    const res = await apiFetch('/registrations', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to submit your enquiry'));
    return normalizeRegistration((await res.json()) as RegistrationRecord);
  },

  updateStatus: async (id: string, status: RegistrationStatus): Promise<RegistrationRecord> => {
    const res = await apiFetch(`/registrations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update status'));
    return normalizeRegistration((await res.json()) as RegistrationRecord);
  },

  delete: async (id: string): Promise<void> => {
    const res = await apiFetch(`/registrations/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) {
      throw new Error(await readApiError(res, 'Failed to delete registration'));
    }
  },
};

export const interestedUsersApi = {
  getAll: async (
    options: { search?: string; status?: 'Interested' | 'Contacted' | 'Converted' | 'all'; signal?: AbortSignal } = {}
  ): Promise<InterestedUserRecord[]> => {
    const query = buildQueryString({
      search: options.search,
      status: options.status && options.status !== 'all' ? options.status : undefined,
    });
    const res = await apiFetch(`/interested-users${query}`, {
      signal: options.signal,
      headers: {},
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to fetch interested users'));
    return res.json();
  },

  create: async (payload: {
    courseId: string;
    name: string;
    email?: string;
    phone: string;
  }): Promise<InterestedUserRecord> => {
    const res = await apiFetch('/interested-users', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to submit your interest'));
    return res.json();
  },

  updateStatus: async (
    id: string,
    status: 'Interested' | 'Contacted' | 'Converted'
  ): Promise<InterestedUserRecord> => {
    const res = await apiFetch(`/interested-users/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to update status'));
    return res.json();
  },

  delete: async (id: string): Promise<void> => {
    const res = await apiFetch(`/interested-users/${id}`, { method: 'DELETE', headers: {} });
    if (!res.ok && res.status !== 204) {
      throw new Error(await readApiError(res, 'Failed to delete record'));
    }
  },
};

export const authApi = {
  getCurrentUser: async () => {
    const res = await apiFetch('/auth/me', { headers: {} });
    if (!res.ok) throw new Error(await readApiError(res, 'No active session.'));
    return (await res.json()) as { user: import('@/types').User };
  },

  login: async (email: string, password: string) => {
    const res = await apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) throw new Error(await readApiError(res, 'Could not log you in.'));
    return (await res.json()) as { user: import('@/types').User };
  },

  logout: async () => {
    const res = await apiFetch('/auth/logout', { method: 'POST', headers: {} });
    if (!res.ok && res.status !== 204) {
      throw new Error(await readApiError(res, 'Could not log you out.'));
    }
  },
};

export const imageApi = {
  uploadImage: async (file: File, folder: ImageUploadFolder): Promise<UploadedImage> => {
    const formData = new FormData();
    formData.append('image', file);

    const res = await apiFetch(`/upload/image?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
      body: formData,
      headers: {},
    });

    if (!res.ok) throw new Error(await readApiError(res, 'Failed to upload image'));
    return res.json();
  },

  getSignedUrl: async (key: string): Promise<string> => {
    const res = await apiFetch(`/images/url/${encodeURIComponent(key)}`, { headers: {} });
    if (!res.ok) throw new Error(await readApiError(res, 'Failed to get signed image URL'));
    const data = (await res.json()) as { url: string };
    return data.url;
  },
};

export const videoApi = {
  uploadVideo: async (file: File, folder: VideoUploadFolder): Promise<UploadedVideo> => {
    const formData = new FormData();
    formData.append('video', file);

    const res = await apiFetch(`/upload/video?folder=${encodeURIComponent(folder)}`, {
      method: 'POST',
      body: formData,
      headers: {},
    });

    if (!res.ok) throw new Error(await readApiError(res, 'Failed to upload video'));
    return res.json();
  },
};
